import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { omit } from 'ramda';
import { withStyles } from '@material-ui/core';
import styles from './styles';
import UserVehiclePhoto from './UserVehiclePhoto';
import DetailForm from '../../common/DetailForm';
import { formMessagesPropTypes } from '../../common/FormMessages';
import Photos from '../../common/Photos';
import TextField from '../../common/TextField';
import AdminAutocomplete from '../../common/AdminAutocomplete';
import * as Yup from 'yup';

/**
 * Form for creating a new vehicle. This is separate from the edit form due to some fields not being editable.
 * Additional field will likely be added later that can only be set at creation.
 */
class NewVehicleForm extends Component {
    state = {
        // User provided photo - NOT editable. 0 to 1 photos supported.
        photoUrl: this.props.formState.photoUrl,
        photoFile: null,

        // Admin photos - editable and 0 to many supported
        photos: this.props.formState.photos || [],
    };

    onSubmit = (data) => {
        this.props.onSubmit && this.props.onSubmit(data, this.state.photos);
    };

    onPhotoChange = (event, setFieldValue) => {
        const files = Array.from(event.target.files);
        const fileURLs = files.map((file) => {
            return {
                photoUrl: URL.createObjectURL(file),
                key: Date.now(),
                file,
            };
        });
        const photos = this.state.photos.concat(fileURLs);
        this.setState({ photos });
        setFieldValue('photos', photos);
    };

    onPhotoClear = (key, setFieldValue) => {
        const photos = this.state.photos.filter((photo) => photo.key !== key);
        this.setState({ photos });
        setFieldValue('photos', photos);
    };

    _getFormFields = () => {
        const { formState, makes, models } = this.props;
        return [
            {
                name: 'photoUrl',
                label: 'User Photo',
                render: (fieldProps) => (
                    <UserVehiclePhoto
                        {...fieldProps}
                        value={formState.photoUrl}
                    />
                ),
            },
            {
                name: 'vehicleName',
                label: 'Name',
                validation: Yup.string().required(),
                render: (fieldProps) => <TextField required {...fieldProps} />,
            },
            {
                name: 'makeId',
                label: 'Make',
                validation: Yup.string().required(),
                render: (fieldProps) => {
                    return (
                        <AdminAutocomplete
                            type="large"
                            required
                            options={makes}
                            {...fieldProps}
                        />
                    );
                },
            },
            {
                name: 'modelId',
                label: 'Model',
                validation: Yup.string().required(),
                render: (fieldProps) => {
                    const { makeId } = fieldProps.values;
                    const options = models.filter(
                        (model) => model.makeId === parseInt(makeId),
                    );
                    return (
                        <AdminAutocomplete
                            type="large"
                            options={options}
                            required
                            {...fieldProps}
                        />
                    );
                },
            },
            {
                name: 'vehicleColor',
                label: 'Color',
                validation: Yup.string().required(),
                render: (fieldProps) => <TextField required {...fieldProps} />,
            },
            {
                name: 'vehiclePlate',
                label: 'License Plate #',
                validation: Yup.string().required(),
                render: (fieldProps) => <TextField required {...fieldProps} />,
            },
            {
                name: 'photos',
                label: 'Admin Photos',
                render: (fieldProps, { setFieldValue }) => (
                    <Photos
                        {...fieldProps} // Field props has to go first so we can override onChange with onPhotoChange.
                        onChange={(event) =>
                            this.onPhotoChange(event, setFieldValue)
                        }
                        onPhotoClear={(key) =>
                            this.onPhotoClear(key, setFieldValue)
                        }
                    />
                ),
            },
        ];
    };

    render() {
        const {
            formState,
            loading,
            messages,
            removeFormMessage,
            makes,
            models,
        } = this.props;
        const fields = this._getFormFields();
        const defaultMakeId = makes[0]?.value || null;
        const makeModels = models.filter(
            (model) => model.makeId === parseInt(defaultMakeId),
        );
        const initFormState = {
            ...omit(['photoUrl', 'UserVehiclePhotoId'], formState),
            makeId: makes[0]?.value || null,
            modelId: makeModels[0]?.value || null,
        };
        return (
            <DetailForm
                initFormState={initFormState}
                onSubmit={this.onSubmit}
                loading={loading}
                fields={fields}
                messages={messages}
                showDelete={false}
                submitButtonText="Create Vehicle"
                removeFormMessage={removeFormMessage}
                FormProps={{ validateOnChange: false, validateOnBlur: false }}
            />
        );
    }
}

NewVehicleForm.propTypes = {
    // From withStyles we expect to get classes
    classes: PropTypes.object.isRequired,

    // Function that will be called when the form is submitted.
    onSubmit: PropTypes.func.isRequired,

    // Bool that determines this form is loading
    loading: PropTypes.bool,

    // The ID Of the vehicle being viewed/edited
    vehicleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    // So you can supply an initial state of the form, if any.
    formState: PropTypes.shape({
        photoUrl: PropTypes.string, // expects url to pull user avatar
        vehicleName: PropTypes.string,
        makeName: PropTypes.string,
        modelName: PropTypes.string,
        vehicleLength: PropTypes.number,
        vehicleHeight: PropTypes.number,
        vehicleColor: PropTypes.string,
        vehiclePlate: PropTypes.string,
        photos: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                photoUrl: PropTypes.string,
                uploadId: PropTypes.number,
            }),
        ),
    }),

    // Messages to be displayed on the form, if any.
    messages: formMessagesPropTypes.messages,

    removeFormMessage: PropTypes.func.isRequired,

    makes: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            makeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            makeName: PropTypes.string,
        }),
    ),
    models: PropTypes.arrayOf(
        PropTypes.shape({
            modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            modelName: PropTypes.string,
            makeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ),
};

NewVehicleForm.defaultProps = {
    formState: {},
    loading: false,
    messages: [],
    makes: [],
    models: [],
};

export default withStyles(styles)(NewVehicleForm);
